import React from 'react';
import { X, Building, Globe, Users, Tag, User, Phone, Mail, Linkedin } from 'lucide-react';

interface MeetingDetailsModalProps {
  meeting: any;
  onClose: () => void;
}

const MeetingDetailsModal: React.FC<MeetingDetailsModalProps> = ({ meeting, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Meeting Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Company Information */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Building size={20} className="mr-2 text-blue-600" />
                Company Information
              </h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Building size={16} className="text-gray-500 mt-1" />
                  <div>
                    <span className="text-sm text-gray-500">Company Name</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{meeting.company_name || 'N/A'}</span>
                      {meeting.company_website && (
                        <a 
                          href={meeting.company_website.startsWith('http') ? meeting.company_website : `https://${meeting.company_website}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Visit Website"
                        >
                          <Globe size={16} />
                        </a>
                      )}
                      {meeting.company_linkedin && (
                        <a 
                          href={meeting.company_linkedin.startsWith('http') ? meeting.company_linkedin : `https://${meeting.company_linkedin}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="LinkedIn Profile"
                        >
                          <Linkedin size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Globe size={16} className="text-gray-500 mt-1" />
                  <div>
                    <span className="text-sm text-gray-500">Country</span>
                    <div className="font-medium text-gray-900">{meeting.country || 'N/A'}</div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Users size={16} className="text-gray-500 mt-1" />
                  <div>
                    <span className="text-sm text-gray-500">Company Size</span>
                    <div className="font-medium text-gray-900">{meeting.employee_headcount || 'N/A'}</div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Tag size={16} className="text-gray-500 mt-1" />
                  <div>
                    <span className="text-sm text-gray-500">Industry</span>
                    <div className="font-medium text-gray-900">{meeting.industry_1 || 'N/A'}</div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Tag size={16} className="text-gray-500 mt-1" />
                  <div>
                    <span className="text-sm text-gray-500">Products & Services</span>
                    <div className="font-medium text-gray-900 break-words">{meeting.products_services || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Person Information */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <User size={20} className="mr-2 text-green-600" />
                Decision Maker
              </h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <User size={16} className="text-gray-500 mt-1" />
                  <div>
                    <span className="text-sm text-gray-500">Name</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{meeting.dm_full_name || 'N/A'}</span>
                      {meeting.dm_linkedin && (
                        <a 
                          href={meeting.dm_linkedin.startsWith('http') ? meeting.dm_linkedin : `https://${meeting.dm_linkedin}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="LinkedIn Profile"
                        >
                          <Linkedin size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Tag size={16} className="text-gray-500 mt-1" />
                  <div>
                    <span className="text-sm text-gray-500">Title</span>
                    <div className="font-medium text-gray-900">{meeting.dm_title || 'N/A'}</div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Phone size={16} className="text-gray-500 mt-1" />
                  <div>
                    <span className="text-sm text-gray-500">Mobile</span>
                    <div className="font-medium text-gray-900">{meeting.dm_mobile_1 || 'N/A'}</div>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Mail size={16} className="text-gray-500 mt-1" />
                  <div>
                    <span className="text-sm text-gray-500">Email</span>
                    <div className="font-medium text-gray-900">{meeting.dm_email_1 || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingDetailsModal;